/**
 * SupplierLoadingPage.jsx
 * ───────────────────────
 * Screen 4 — Flatbed/OTHER loading screen.
 * Route: /supplier/disposal/:projectId/load/:bolId?
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import {
  getSupplierProjects, getSupplierToolPool,
  checkFrostDismissal, dismissFrost,
} from '../../api/supplierDisposalApi';
import { useDisposalOfflineQueue } from '../../hooks/useDisposalOfflineQueue';
import { matchesCanonicalSearch } from '../../shared/searchFilter';
import { useBolSequence } from '../../hooks/useBolSequence';
import SupplierLayout from '../../layouts/SupplierLayout';
import ShipmentModal from '../../components/supplier/ShipmentModal';
import WeightFooter from '../../components/supplier/WeightFooter';
import FrostDismissalBanner from '../../components/supplier/FrostDismissalBanner';
import './SupplierLoadingPage.css';

const FROST_STATES = new Set([
  'idaho','illinois','indiana','iowa','maine','michigan','minnesota','montana',
  'new hampshire','north dakota','ohio','pennsylvania','south dakota','vermont',
  'wisconsin','wyoming','id','il','in','ia','me','mi','mn','mt','nh','nd',
  'oh','pa','sd','vt','wi','wy',
]);

function isFrostSeason() {
  const m = new Date().getMonth() + 1;
  return m >= 2 && m <= 6;
}

const WEIGHT_RANGES = [
  { label: 'All', value: 'ALL' },
  { label: 'Under 2,000', value: 'U2K', min: 0, max: 2000 },
  { label: '2,000–5,000', value: '2K5K', min: 2000, max: 5000 },
  { label: '5,000–10,000', value: '5K10K', min: 5000, max: 10000 },
  { label: 'Over 10,000', value: 'O10K', min: 10000, max: Infinity },
];

/* The five location numbers render together in the detail panel when any one has
   a value, and not at all when none does. Has-value test, so "0" renders as 0. */
const NBR_KEYS = ['row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr'];
const hasValue = v => v !== null && v !== undefined && v !== '';
const nbr = v => (hasValue(v) ? String(v) : '—');

export default function SupplierLoadingPage() {
  const { projectId, bolId: urlBolId } = useParams();
  const navigate = useNavigate();
  const { callerEmail } = useUser();
  const { queueBolCreate, queueAddTool, queueRemoveTool } = useDisposalOfflineQueue(callerEmail);
  const { getNextBolNumber } = useBolSequence();

  // Data
  const [project, setProject] = useState(null);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session — restore from sessionStorage on mount
  const sessionKey = `tmg_loading_session_${projectId}`;

  function loadPersistedSession() {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  }

  const persisted = loadPersistedSession();
  const [activeSession, setActiveSession] = useState(() => {
    if (persisted?.activeSession && (!urlBolId || persisted.activeSession.bolId === urlBolId)) {
      return persisted.activeSession;
    }
    return null;
  });
  const [loadedToolIds, setLoadedToolIds] = useState(() => {
    if (persisted?.loadedToolIds && (!urlBolId || persisted?.activeSession?.bolId === urlBolId)) {
      return new Set(persisted.loadedToolIds);
    }
    return new Set();
  });
  const [stagedToolIds, setStagedToolIds] = useState(new Set()); // TODO: session-only, persist in Phase 5
  const [shipmentModalOpen, setShipmentModalOpen] = useState(false);
  const [pendingToolId, setPendingToolId] = useState(null);

  // Persist session to sessionStorage on every change
  useEffect(() => {
    if (activeSession) {
      try {
        sessionStorage.setItem(sessionKey, JSON.stringify({
          activeSession,
          loadedToolIds: Array.from(loadedToolIds),
        }));
      } catch { /* ignore */ }
    }
  }, [activeSession, loadedToolIds, sessionKey]);

  // Detail sheet
  const [detailTool, setDetailTool] = useState(null);

  // Frost
  const [frostBannerVisible, setFrostBannerVisible] = useState(false);

  // Filters
  const [filters, setFilters] = useState({ siteLoc: 'ALL', staged: 'ALL', weight: 'ALL', search: '' });
  const [searchOpen, setSearchOpen] = useState(false);

  // ── Load data on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!callerEmail || !projectId) return;
    setLoading(true);
    setError(null);

    Promise.all([
      getSupplierProjects(callerEmail),
      getSupplierToolPool(callerEmail, projectId),
    ])
      .then(([projData, toolData]) => {
        const projList = Array.isArray(projData) ? projData : projData.projects || projData.results || [];
        const match = projList.find(p => p.project_id === projectId);
        setProject(match || null);

        const toolList = Array.isArray(toolData) ? toolData : toolData.results || [];
        setTools(toolList);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [callerEmail, projectId]);

  // Session restore is handled via sessionStorage in useState initializers above.
  // No API fetch needed — the BOL may be offline-queued and not on server yet.

  // ── Frost check ────────────────────────────────────────────────────
  useEffect(() => {
    if (!project || !callerEmail) return;
    const state = (project.site_state || project.site_state_province || '').trim().toLowerCase();
    if (!isFrostSeason() || !FROST_STATES.has(state)) return;
    checkFrostDismissal(callerEmail, projectId)
      .then(r => { if (!r.dismissed) setFrostBannerVisible(true); })
      .catch(() => { /* non-blocking */ });
  }, [project, callerEmail, projectId]);

  // ── Derived data ───────────────────────────────────────────────────
  const siteLocOptions = useMemo(() => {
    const set = new Set();
    tools.forEach(t => { if (t.site_location) set.add(t.site_location); });
    return [...set].sort();
  }, [tools]);

  const filteredTools = useMemo(() => {
    return tools.filter(t => {
      const tid = t.id || t.tool_id;
      if (filters.siteLoc !== 'ALL' && t.site_location !== filters.siteLoc) return false;
      if (filters.staged === 'Staged' && !stagedToolIds.has(tid)) return false;
      if (filters.staged === 'Not Staged' && stagedToolIds.has(tid)) return false;
      if (filters.weight !== 'ALL') {
        const w = t.estimated_weight || t.weight_lbs || 0;
        const range = WEIGHT_RANGES.find(r => r.value === filters.weight);
        if (range && (w < range.min || w >= range.max)) return false;
      }
      // Canonical client-side search over the offline PROJECT_TOOLS snapshot.
      // Session B widened that payload to the 25 canonical fields +
      // part_numbers[]/asset_tags[], so the shared gate now matches the full
      // set (raw snake_case → no fieldNameMap). Sub-min queries match all.
      if (filters.search && !matchesCanonicalSearch(t, filters.search)) return false;
      return true;
    });
  }, [tools, filters, stagedToolIds]);

  const loadedWeight = useMemo(() => {
    let total = 0;
    for (const tid of loadedToolIds) {
      const t = tools.find(x => (x.id || x.tool_id) === tid);
      if (t) total += (t.estimated_weight || t.weight_lbs || 0);
    }
    return total;
  }, [loadedToolIds, tools]);

  const target = project?.target_tool_count || 0;
  const shipped = project?.shipped_tool_count || 0;

  // ── Handlers ───────────────────────────────────────────────────────
  const handleToolTap = useCallback((toolId) => {
    if (loadedToolIds.has(toolId)) {
      // Remove
      setLoadedToolIds(prev => { const next = new Set(prev); next.delete(toolId); return next; });
      if (activeSession) {
        queueRemoveTool({
          bol_id: activeSession.bolId, project_id: projectId,
          tool_id: toolId, removed_by: callerEmail,
          removed_reason: 'Removed by operator during loading',
        });
      }
    } else {
      // Add
      if (!activeSession) {
        setPendingToolId(toolId);
        setShipmentModalOpen(true);
        return;
      }
      setLoadedToolIds(prev => new Set(prev).add(toolId));
      queueAddTool({
        bol_id: activeSession.bolId, project_id: projectId,
        tool_id: toolId, added_by: callerEmail,
      });
    }
  }, [loadedToolIds, activeSession, projectId, callerEmail, queueAddTool, queueRemoveTool]);

  const handleSelectAll = useCallback(() => {
    if (!activeSession) {
      setShipmentModalOpen(true);
      return;
    }
    const newIds = new Set(loadedToolIds);
    for (const t of filteredTools) {
      const tid = t.id || t.tool_id;
      if (!newIds.has(tid)) {
        newIds.add(tid);
        queueAddTool({
          bol_id: activeSession.bolId, project_id: projectId,
          tool_id: tid, added_by: callerEmail,
        });
      }
    }
    setLoadedToolIds(newIds);
  }, [activeSession, loadedToolIds, filteredTools, projectId, callerEmail, queueAddTool]);

  const handleShipmentStart = useCallback((shipmentData) => {
    const bolId = crypto.randomUUID();
    const bolNumber = getNextBolNumber(projectId, project?.project_number || projectId);

    const session = { bolId, bolNumber, ...shipmentData };
    setActiveSession(session);

    queueBolCreate({
      bol_id: bolId, bol_number: bolNumber, project_id: projectId,
      site_id: project?.site_id || '', truck_type: shipmentData.truckType,
      hauler_company: shipmentData.haulerCompany, truck_number: shipmentData.truckNumber,
      driver_name: shipmentData.driverName, advisory_weight_lbs: shipmentData.advisoryWeightLbs,
      source: 'OPERATOR', created_by: callerEmail,
    });

    setShipmentModalOpen(false);

    // Process pending tool
    if (pendingToolId) {
      setLoadedToolIds(prev => new Set(prev).add(pendingToolId));
      queueAddTool({
        bol_id: bolId, project_id: projectId,
        tool_id: pendingToolId, added_by: callerEmail,
      });
      setPendingToolId(null);
    }

    // Update URL
    navigate(`/supplier/disposal/${projectId}/load/${bolId}`, { replace: true });
  }, [projectId, project, callerEmail, pendingToolId, getNextBolNumber, queueBolCreate, queueAddTool, navigate]);

  const handleFrostDismiss = useCallback(() => {
    dismissFrost(callerEmail, projectId, project?.site_id || '').catch(() => {});
    setFrostBannerVisible(false);
  }, [callerEmail, projectId, project]);

  function toggleStaged(toolId) {
    setStagedToolIds(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId); else next.add(toolId);
      return next;
    });
  }

  const oem = project?.oem || '';
  const projectNumber = project?.project_number || projectId;

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="slp-hdr">
        <button className="slp-hdr-back" onClick={() => navigate('/supplier/disposal')}>&larr;</button>
        <span className="slp-hdr-title">{oem ? `${oem} — ${projectNumber}` : projectNumber}</span>
        <span className="slp-hdr-logo">TMG</span>
      </div>

      {/* ── Session badge ─────────────────────────────────────────────── */}
      {activeSession && (
        <div className="slp-session-bar">
          <span className="slp-session-bol">{activeSession.bolNumber}</span>
          <span className="slp-session-truck">
            {activeSession.truckType} — {activeSession.haulerCompany}
          </span>
        </div>
      )}

      {/* ── Frost banner ──────────────────────────────────────────────── */}
      <FrostDismissalBanner
        isVisible={frostBannerVisible}
        stateName={project?.site_state || project?.site_state_province || ''}
        onDismiss={handleFrostDismiss}
      />

      {/* ── Filters ───────────────────────────────────────────────────── */}
      <div className="slp-filters">
        <select className="slp-filter-sel" value={filters.siteLoc}
          onChange={e => setFilters(p => ({ ...p, siteLoc: e.target.value }))}>
          <option value="ALL">All Locations</option>
          {siteLocOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="slp-filter-sel" value={filters.staged}
          onChange={e => setFilters(p => ({ ...p, staged: e.target.value }))}>
          <option value="ALL">All</option>
          <option value="Staged">Staged</option>
          <option value="Not Staged">Not Staged</option>
        </select>
        <select className="slp-filter-sel" value={filters.weight}
          onChange={e => setFilters(p => ({ ...p, weight: e.target.value }))}>
          {WEIGHT_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <button className="slp-search-btn" onClick={() => setSearchOpen(p => !p)}>
          {searchOpen ? '✕' : '🔍'}
        </button>
      </div>

      {searchOpen && (
        <div className="slp-search-row">
          <input type="text" className="slp-search-input" placeholder="Search tool ID..."
            value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            autoFocus />
        </div>
      )}

      {/* ── Summary strip ─────────────────────────────────────────────── */}
      <div className="slp-summary">
        <span className="slp-summary-counts">
          Target: {target} &middot; Shipped: {shipped} &middot; Balance: {target - shipped}
        </span>
        <label className="slp-select-all">
          <input type="checkbox" onChange={handleSelectAll} checked={false} />
          <span>Select All</span>
        </label>
      </div>

      {/* ── Tool list ─────────────────────────────────────────────────── */}
      <div className="slp-tool-list">
        {loading ? (
          <div className="slp-state">Loading tools...</div>
        ) : error ? (
          <div className="slp-state slp-state-err">{error}</div>
        ) : filteredTools.length === 0 ? (
          <div className="slp-state">No tools match your filters.</div>
        ) : (
          filteredTools.map(t => {
            const tid = t.id || t.tool_id;
            const isLoaded = loadedToolIds.has(tid);
            const isStaged = stagedToolIds.has(tid);
            const weight = t.estimated_weight || t.weight_lbs || 0;
            const imgUrl = (t.image_urls || [])[0] || null;

            return (
              <div key={tid} className={`slp-tool-row ${isLoaded ? 'slp-tool-loaded' : ''}`}>
                {/* Thumbnail */}
                <div className="slp-tool-thumb" onClick={() => setDetailTool(t)}>
                  {imgUrl ? (
                    <img src={imgUrl} alt="" className="slp-tool-img" loading="lazy" />
                  ) : (
                    <div className="slp-tool-img-ph">📷</div>
                  )}
                </div>

                {/* Info */}
                <div className="slp-tool-info" onClick={() => handleToolTap(tid)}>
                  <div className="slp-tool-id">{t.supplier_tool_id || tid}</div>
                  <div className="slp-tool-loc">
                    {[t.site_location, t.detail_location].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>

                {/* Staged */}
                <input type="checkbox" className="slp-staged-cb" checked={isStaged}
                  onChange={() => toggleStaged(tid)} />

                {/* Weight */}
                <span className="slp-tool-weight">
                  {weight ? `${Number(weight).toLocaleString()} lbs` : '— lbs'}
                </span>

                {/* Add/Remove button */}
                <button
                  className={`slp-add-btn ${isLoaded ? 'slp-add-loaded' : ''}`}
                  onClick={() => handleToolTap(tid)}
                >
                  {isLoaded ? '✓' : '+'}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* ── Weight footer ─────────────────────────────────────────────── */}
      <WeightFooter
        loadedWeightLbs={loadedWeight}
        advisoryWeightLbs={activeSession?.advisoryWeightLbs ?? 48000}
        toolCount={loadedToolIds.size}
        reviewBolDisabled={loadedToolIds.size === 0}
        onReviewBol={() => {
          if (activeSession) {
            navigate(`/supplier/disposal/${projectId}/bol/${activeSession.bolId}/preview`, {
              state: {
                bol: {
                  bol_id: activeSession.bolId,
                  bol_number: activeSession.bolNumber,
                  truck_type: activeSession.truckType,
                  hauler_company: activeSession.haulerCompany,
                  truck_number: activeSession.truckNumber,
                  driver_name: activeSession.driverName,
                  advisory_weight_lbs: activeSession.advisoryWeightLbs,
                  tools: Array.from(loadedToolIds).map(tid => ({ tool_id: tid, removed_at: null })),
                },
                project,
                loadedTools: tools.filter(t => loadedToolIds.has(t.id || t.tool_id)),
                isAmendedBol: false,
              },
            });
          }
        }}
      />

      {/* ── Shipment modal ────────────────────────────────────────────── */}
      <ShipmentModal
        isOpen={shipmentModalOpen}
        onStart={handleShipmentStart}
        onCancel={() => { setShipmentModalOpen(false); setPendingToolId(null); }}
        projectNumber={projectNumber}
      />

      {/* ── Tool detail sheet ─────────────────────────────────────────── */}
      {detailTool && (
        <div className="slp-detail-overlay" onClick={() => setDetailTool(null)}>
          <div className="slp-detail-sheet" onClick={e => e.stopPropagation()}>
            {/* Photos */}
            <div className="slp-detail-photos">
              {(detailTool.image_urls || []).length > 0 ? (
                <div className="slp-detail-photo-strip">
                  {detailTool.image_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="slp-detail-photo" loading="lazy" />
                  ))}
                </div>
              ) : (
                <div className="slp-detail-no-photo">No photos</div>
              )}
            </div>

            <div className="slp-detail-id">{detailTool.supplier_tool_id || detailTool.id}</div>

            <div className="slp-detail-grid">
              <div className="slp-detail-field"><span className="slp-detail-label">OEM</span><span>{detailTool.oem || '—'}</span></div>
              <div className="slp-detail-field"><span className="slp-detail-label">Site Location</span><span>{detailTool.site_location || '—'}</span></div>
              <div className="slp-detail-field"><span className="slp-detail-label">Detail Location</span><span>{detailTool.detail_location || '—'}</span></div>
              {NBR_KEYS.some(k => hasValue(detailTool[k])) && (
                <>
                  <div className="slp-detail-field"><span className="slp-detail-label">Row Nbr</span><span>{nbr(detailTool.row_nbr)}</span></div>
                  <div className="slp-detail-field"><span className="slp-detail-label">Stack Nbr</span><span>{nbr(detailTool.stack_nbr)}</span></div>
                  <div className="slp-detail-field"><span className="slp-detail-label">Rack Nbr</span><span>{nbr(detailTool.rack_nbr)}</span></div>
                  <div className="slp-detail-field"><span className="slp-detail-label">Shelf Nbr</span><span>{nbr(detailTool.shelf_nbr)}</span></div>
                  <div className="slp-detail-field"><span className="slp-detail-label">Skid Nbr</span><span>{nbr(detailTool.skid_nbr)}</span></div>
                </>
              )}
              <div className="slp-detail-field"><span className="slp-detail-label">Est. Weight</span><span>{detailTool.estimated_weight ? `${Number(detailTool.estimated_weight).toLocaleString()} lbs` : '—'}</span></div>
              <div className="slp-detail-field"><span className="slp-detail-label">Disposal Status</span><span>{detailTool.disposal_status || '—'}</span></div>
            </div>

            <label className="slp-detail-staged">
              <input type="checkbox" checked={stagedToolIds.has(detailTool.id || detailTool.tool_id)}
                onChange={() => toggleStaged(detailTool.id || detailTool.tool_id)} />
              <span>Staged</span>
            </label>

            {loadedToolIds.has(detailTool.id || detailTool.tool_id) ? (
              <button className="slp-detail-btn slp-detail-btn-danger" onClick={() => {
                handleToolTap(detailTool.id || detailTool.tool_id);
                setDetailTool(null);
              }}>Remove from Load</button>
            ) : (
              <button className="slp-detail-btn slp-detail-btn-primary" onClick={() => {
                handleToolTap(detailTool.id || detailTool.tool_id);
                setDetailTool(null);
              }}>Add to Load</button>
            )}
          </div>
        </div>
      )}
    </SupplierLayout>
  );
}
